import { useEffect, useState } from "react"
import SwitchInput from "../../shared/Inputs/SwitchInput"
import { useForm } from "react-hook-form"
import GeneralPriceSettingSingle from "../PriceConfiguration/GeneralPriceSettingSingle"
import { useDebounce } from "@uidotdev/usehooks"
import usePriceSettingsMutations from "../../../data/usePriceSettingsMutations"
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers"
import SelectInput from "../../shared/Inputs/SelectInput"
import useUkvdLookup from "../../../data/useVehicleLookup"
import ComboBoxInput from "../../shared/Inputs/ComboBoxInput"
import LoadingWheel from "../../shared/LoadingWheel"

export default function SpecificPriceCell({ 
  priceSetting,
  prop,
  type,
  info,
  width,
  bgColor,
  options,
  minValue = null,
  disabled = false,
}: { priceSetting: any, prop: string, type: string, info: any, width?: string, bgColor?: string, options?: any[], minValue?: number | null, disabled?: boolean }) {
  const [inFocus, setInFocus] = useState(false);
  const { makes, ranges } = useUkvdLookup(inFocus ? "cell" : undefined, prop == "model" ? (priceSetting.make) : undefined);
  const [comboOptions, setComboOptions] = useState<any[]>([]);

  const { watch, register, setValue } = useForm<any>({
    defaultValues: { ...priceSetting }
  });

  const { updateSpecific } = usePriceSettingsMutations();
  const { saveHandlers   } = useDefaultCRUDHandlers("Specific Price Setting");

  const value   = type === "price" ? watch(`${prop}.value`)   : watch(prop);
  const isFixed = type === "price" ? watch(`${prop}.isFixed`) : null;

  const debouncedValue = useDebounce(value, 500);

  const disabledClasses = disabled ? "pointer-events-none opacity-50" : "";

  useEffect(() => {
    // console.log("Debounced Value Changed:", debouncedValue);
    if (debouncedValue === undefined) return;

    var updatedSetting = { ...priceSetting };

    // need to clean price setting values from '' to null
    for (const key in updatedSetting) {
      if (updatedSetting[key] === '') {
        updatedSetting[key] = null;
      }
    }
     
    if (type === "price") {
      // console.log("Processing Price Update:", debouncedValue, isFixed);
      var fixedValue = (isFixed == "true" || isFixed == true)
      var priceSettingValue   = priceSetting[prop]?.value   ?? null;
      var priceSettingIsFixed = priceSetting[prop]?.isFixed == null ? false : priceSetting[prop]?.isFixed;

      // console.log("Comparing Price Update:", debouncedValue, priceSettingValue, fixedValue, priceSettingIsFixed);

      if (debouncedValue == priceSettingValue && fixedValue == priceSettingIsFixed) return;
       if (debouncedValue == null && priceSettingValue == null) return;

      // console.log("Price Updated", priceSetting[prop]?.value + " to " + debouncedValue + ", isFixed: " + priceSetting[prop]?.isFixed + " to " + fixedValue);

      if (debouncedValue == null) updatedSetting = { ...updatedSetting, [prop]: null }
      else updatedSetting = { ...updatedSetting, [prop]: { value: debouncedValue, isFixed: isFixed } }
    } else if (type === "combo") {
      let value = debouncedValue?.id ?? debouncedValue ?? "";

      if ((value) == (priceSetting[prop] ?? "")) return;
      updatedSetting = { ...updatedSetting, [prop]: value }

      // console.log("Combo Updated", priceSetting[prop] + " to " + value);
    } else if (type === "switch") {
      if (debouncedValue == priceSetting[prop]) return;
      updatedSetting = { ...updatedSetting, [prop]: debouncedValue == null || debouncedValue == '' ? false : debouncedValue  }
    }else {
      if (debouncedValue == priceSetting[prop]) return;
      // console.log("Simple Updated", priceSetting[prop] + " to " + debouncedValue);
      updatedSetting = { ...updatedSetting, [prop]: debouncedValue == undefined || debouncedValue == null || debouncedValue == '' ? null : debouncedValue  }
    }

    if (debouncedValue != '' && debouncedValue != null && debouncedValue != undefined && minValue !== null && debouncedValue < minValue) {
      saveHandlers.onError();
    }

    info.table.options.meta?.updateData(info.row.index, info.column.id, debouncedValue == undefined || debouncedValue == null || debouncedValue == '' ? null : debouncedValue);

    // console.log("Updating Specific Price Setting of type", type, "for prop", prop, "from value", priceSetting[prop], "to value:", debouncedValue, "isFixed:", isFixed);
    updateSpecific.mutate(updatedSetting, {
      onError  : (error: any) => saveHandlers.onError(),
      onSuccess: (data: any ) => saveHandlers.onSuccess(),
    });
    
  }, [debouncedValue, isFixed])

  useEffect(() => {
    if (type == "select") setValue(prop, priceSetting[prop] ?? (options && options.length > 0 ? options[0].id : 0));
    if (type == "price" ) setValue(`${prop}.isFixed`, priceSetting[prop]?.isFixed ?? true);
    if (type == "combo" ) setValue(prop, { id: priceSetting[prop] ?? '', name: priceSetting[prop] ?? '' });
    // else setValue(prop, priceSetting[prop] ?? '');
  }, [])

  useEffect(() => {
    if (type !== "combo") return;

    if (prop == "make" && !makes.isLoading && makes.data) {
      const makeOptions = makes.data.map(m => ({ id: m, name: m })) ?? [];
      setComboOptions(makeOptions);
    }

    if (prop == "model" && !ranges.isLoading && ranges.data) {
      const modelOptions = ranges.data.map(r => ({ id: r, name: r })) ?? [];
      setComboOptions(modelOptions);
    }

  }, [makes.isLoading, makes.data, ranges.isLoading, ranges.data])

  if (type === "combo") {
    return (<span className={`flex p-1 relative ${bgColor ? bgColor : ''} ${width ? width : 'max-w-24'}`}>
      <ComboBoxInput
        label={undefined}
        registerName={prop}
        register={register}
        setValue={setValue}
        options={comboOptions && comboOptions.length > 0 
          ? comboOptions 
          : [priceSetting[prop] 
            ? { id: priceSetting[prop], name: priceSetting[prop] } 
            : { id: 0, name: '' }]
        }
        searchField={"name"}
        initialValue={priceSetting[prop] ?? ""}
        onFocus={() => setInFocus(true)}
        onBlur={()  => setInFocus(false)}
        className={`!py-1 !text-xs !pl-2 !pr-6 ${disabledClasses}`}
      />
      {inFocus && ((prop == "make" && makes.isLoading) || (prop == "model" && ranges.isLoading)) && (
        <span className="absolute top-[-6px] right-3 h-5 w-5">
          <LoadingWheel />
        </span>
      )}
    </span>)
  }

  if (type === "switch") {
    return (<span className={`flex p-1 ${bgColor ? bgColor : ''}`}><SwitchInput label={undefined} registerName={prop} register={register} setValue={setValue} watch={watch}/></span>)
  }

  if (type == "select") {
    return (<span className={`flex p-1 w-24 ${bgColor ? bgColor : ''}`}>
      <SelectInput 
        label={undefined}
        registerName={prop}
        register={register}
        setValue={setValue}
        watch={watch}
        options={options ?? []}
        fontSize="text-xs"
        classes={`!p-1 ${disabledClasses}`}
        cols="w-full"
        optionsContainerClasses="!w-32"
      />
    </span>)
  }

  if (type === "price") {
    return (<span className={`flex p-1 ${bgColor ? bgColor : ''}`}>
      <GeneralPriceSettingSingle 
        label={undefined} 
        objectName={prop} 
        register={register} 
        setValue={setValue} 
        watch={watch} 
        minVal={minValue} 
        width="w-14" 
        containerClasses={`gap-x-[0px] ${disabledClasses}`}
        customInputClasses="!text-xs !w-14 !p-1" 
        customRadioClasses="!p-[4px] !px-[5px] !text-xs !rounded-l-none"
        customRadioContainerClasses="mt-[0px] !p-[0px] ml-[-2px]"
      />
    </span>)
  }

  return (
    <span className={`flex p-1 ${bgColor ? bgColor : ''}`} >
      <input 
        type={type ? type : "text"}
        className={`border border-gray-300 rounded-md p-1 text-center w-auto ${width ? width : 'max-w-16'} ${minValue !== null ? (value < minValue ? 'border-red-500 bg-red-500 text-white' : '') : ''} text-xs ${disabledClasses}`}
        // value={prop in priceSetting ? (priceSetting[prop] ?? "") : ''}
        {...register(prop)}
        min={minValue !== null ? minValue : undefined}
      />
    </span>
  )
}
